import React from 'react';
import { useNavigate } from 'react-router-dom';

// --- COMPONENTE SELECTOR DE FECHAS EXCLUSIVO DE FINANZAS ---
import DateRangePickerFinanzas from './DateRangePickerFinanzas';

// --- SIDEBAR DE FILTROS Y VISTAS ---
import FiltrosSidebarFinanzas from './FiltrosSidebarFinanzas';
import TablePreviewFinanzas from '../ReportesFacturacion/TablePreviewFinanzas';
import { formatCurrency } from '../helpers';

// --- CUSTOM HOOK DE LOGICA DE FINANZAS ---
import { useReportesFinanzas } from './useReportesFinanzas';

const IconArrowLeft = () => (
    <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
    </svg>
);

const IconDatabase = () => (
    <svg className="w-12 h-12 text-[#01032d] opacity-90 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
    </svg>
);

const ReportesFinanzas = ({ maestros, onBack }) => {
    const navigate = useNavigate();

    const handleVolver = () => {
        if (onBack) {
            onBack(); // Resetea el módulo activo en FacturacionGestionPage
        } else {
            navigate('/admin/facturacion-gestion');
        }
    };

    const {
        fechaDesde, setFechaDesde,
        fechaHasta, setFechaHasta,
        selectedMonth, setSelectedMonth,
        selectedYear, setSelectedYear,
        loadingProgress,
        isDbLoaded,
        hasQueried,
        moduloRaiz, setModuloRaiz,
        subVista, setSubVista,
        columnasSeleccionadas,
        busquedaGlobal, setBusquedaGlobal,
        multiFiltros, setMultiFiltros,
        camposDisponibles,
        opcionesFiltrosUnicas,
        registrosProcesados,
        consultarReporte,
        acumuladosIVA,
        reportesConciliacion,
        cashFlowData,
        retencionesDiscriminadas
    } = useReportesFinanzas();

    return (
        <div className="flex flex-col h-screen w-full bg-[#f8fafc] text-slate-800 overflow-hidden font-sans text-left antialiased">
            <div className="h-1 bg-[#01032d] w-full shrink-0"></div>

            {/* HEADER SUPERIOR */}
            <header className="h-20 shrink-0 bg-white border-b border-slate-200/80 px-8 flex items-center justify-between z-40 relative shadow-sm">
                <div className="flex items-center gap-5">
                    <button 
                        type="button"
                        onClick={handleVolver}
                        className="group p-2.5 text-slate-500 hover:text-slate-800 hover:bg-slate-50 rounded-xl border border-slate-200 shadow-sm transition-all"
                    >
                        <IconArrowLeft />
                    </button>
                    <div>
                        <h2 className="text-[17px] font-black text-[#01032d] tracking-tight uppercase leading-none">
                            Auditoría & Balance Financiero
                        </h2>
                        <p className="text-[10px] text-slate-400 font-extrabold uppercase tracking-widest mt-1.5 flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                            Gestión de Tesorería, IVA y Cash Flow
                        </p>
                    </div>
                </div>

                <DateRangePickerFinanzas 
                    fechaDesde={fechaDesde}
                    setFechaDesde={setFechaDesde}
                    fechaHasta={fechaHasta}
                    setFechaHasta={setFechaHasta}
                    selectedMonth={selectedMonth}
                    setSelectedMonth={setSelectedMonth}
                    selectedYear={selectedYear}
                    setSelectedYear={setSelectedYear}
                    onSubmit={consultarReporte}
                />
            </header>

            {/* CONTENEDOR PRINCIPAL */}
            <div className="flex-grow flex overflow-hidden">
                <FiltrosSidebarFinanzas 
                    moduloRaiz={moduloRaiz}
                    setModuloRaiz={setModuloRaiz}
                    isDbLoaded={isDbLoaded}
                    opcionesFiltrosUnicas={opcionesFiltrosUnicas}
                    multiFiltros={multiFiltros}
                    setMultiFiltros={setMultiFiltros}
                />

                <main className="flex-grow flex flex-col overflow-hidden bg-[#f1f5f9]">
                    
                    {/* BARRA DE SELECCIÓN DE SUB-VISTAS */}
                    {hasQueried && isDbLoaded && (
                        <div className="bg-white border-b border-slate-200 px-6 h-14 flex items-center justify-between shrink-0 shadow-xs">
                            <div className="flex bg-slate-100/80 p-1 rounded-xl gap-1">
                                {moduloRaiz === 'LIBRO_IVA' && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setSubVista('DEBITO')}
                                            className={`px-3.5 py-1.5 text-xs font-bold uppercase rounded-lg transition-all ${
                                                subVista === 'DEBITO' ? 'bg-[#01032d] text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'
                                            }`}
                                        >
                                            1. Facturas Emitidas (Débito)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSubVista('CREDITO')}
                                            className={`px-3.5 py-1.5 text-xs font-bold uppercase rounded-lg transition-all ${
                                                subVista === 'CREDITO' ? 'bg-[#01032d] text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'
                                            }`}
                                        >
                                            2. Compras / Factura A (Crédito)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSubVista('RETENCIONES_IVA')}
                                            className={`px-3.5 py-1.5 text-xs font-bold uppercase rounded-lg transition-all ${
                                                subVista === 'RETENCIONES_IVA' ? 'bg-[#01032d] text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'
                                            }`}
                                        >
                                            3. Retenciones IVA
                                        </button>
                                    </>
                                )}

                                {moduloRaiz === 'CONCILIACIONES' && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setSubVista('COBROS')}
                                            className={`px-3.5 py-1.5 text-xs font-bold uppercase rounded-lg transition-all ${
                                                subVista === 'COBROS' ? 'bg-[#01032d] text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'
                                            }`}
                                        >
                                            1. Cobros (Facturas Emitidas)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSubVista('PAGOS')}
                                            className={`px-3.5 py-1.5 text-xs font-bold uppercase rounded-lg transition-all ${
                                                subVista === 'PAGOS' ? 'bg-[#01032d] text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'
                                            }`}
                                        >
                                            2. Pagos (Gastos & Compras)
                                        </button>
                                    </>
                                )}

                                {moduloRaiz === 'CASH_FLOW' && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setSubVista('INGRESOS')}
                                            className={`px-3.5 py-1.5 text-xs font-bold uppercase rounded-lg transition-all ${
                                                subVista === 'INGRESOS' ? 'bg-[#01032d] text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'
                                            }`}
                                        >
                                            1. Acreditaciones CBU (Ingresos)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSubVista('EGRESOS')}
                                            className={`px-3.5 py-1.5 text-xs font-bold uppercase rounded-lg transition-all ${
                                                subVista === 'EGRESOS' ? 'bg-[#01032d] text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'
                                            }`}
                                        >
                                            2. Débitos CBU (Egresos & Cheques)
                                        </button>
                                    </>
                                )}

                                {moduloRaiz === 'RETENCIONES' && (
                                    <div className="px-3 py-1 text-xs font-black text-[#01032d] uppercase tracking-wider flex items-center gap-2">
                                        <span>🛡️</span> Retenciones Sufridas Discriminadas
                                    </div>
                                )}
                            </div>

                            <div className="w-72">
                                <input 
                                    type="text" 
                                    placeholder="Buscar en la tabla..." 
                                    value={busquedaGlobal} 
                                    onChange={(e) => setBusquedaGlobal(e.target.value)} 
                                    className="w-full bg-slate-50 text-xs border border-slate-200 rounded-xl pl-3 pr-3 py-1.5 outline-none focus:bg-white focus:border-[#01032d]" 
                                />
                            </div>
                        </div>
                    )}

                    {/* VISTA PRINCIPAL CONTENEDORA */}
                    <div className="flex-grow flex flex-col overflow-hidden p-6 md:p-8 space-y-4">
                        
                        {/* CARDS LIBRO IVA */}
                        {hasQueried && isDbLoaded && moduloRaiz === 'LIBRO_IVA' && acumuladosIVA && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 shrink-0">
                                <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Débito Fiscal</span>
                                    <div className="text-lg font-black font-mono text-slate-900 mt-1">
                                        {formatCurrency(acumuladosIVA.totalIvaDebitoARS, 'ARS')}
                                    </div>
                                </div>
                                <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Crédito Fiscal</span>
                                    <div className="text-lg font-black font-mono text-slate-900 mt-1">
                                        {formatCurrency(acumuladosIVA.totalIvaCreditoARS, 'ARS')}
                                    </div>
                                </div>
                                <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
                                    <span className="text-[10px] font-bold text-rose-600 uppercase tracking-widest block">Retenciones IVA</span>
                                    <div className="text-lg font-black font-mono text-rose-700 mt-1">
                                        -{formatCurrency(acumuladosIVA.totalRetencionesIvaARS, 'ARS')}
                                    </div>
                                </div>
                                <div className="bg-slate-900 text-white p-4 rounded-2xl shadow-xs">
                                    <span className="text-[10px] font-bold uppercase tracking-widest opacity-70 block">Posición IVA</span>
                                    <div className="text-lg font-black font-mono text-emerald-400 mt-1">
                                        ARS: {formatCurrency(Math.abs(acumuladosIVA.posicionIvaARS), 'ARS')}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* CARDS CONCILIACIONES */}
                        {hasQueried && isDbLoaded && moduloRaiz === 'CONCILIACIONES' && reportesConciliacion && (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 shrink-0">
                                <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Monto No Conciliado</span>
                                    <div className="text-lg font-black font-mono text-slate-900 mt-1">
                                        ARS: {formatCurrency(reportesConciliacion.montoTotalNoConciliadoARS, 'ARS')}
                                    </div>
                                </div>
                                <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Desfases Detectados</span>
                                    <div className="text-lg font-black font-mono text-rose-600 mt-1">
                                        {reportesConciliacion.comprobantesConDesfaseCount} <span className="text-xs font-normal text-slate-400">casos</span>
                                    </div>
                                </div>
                                <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Salud Conciliación</span>
                                    <div className="text-lg font-black font-mono text-emerald-600 mt-1">
                                        {reportesConciliacion.porcentajeSaludConciliacion}%
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* CARDS CASH FLOW BANCO */}
                        {hasQueried && isDbLoaded && moduloRaiz === 'CASH_FLOW' && cashFlowData && (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 shrink-0">
                                <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Ingreso Efectivo CBU</span>
                                    <div className="text-lg font-black font-mono text-emerald-600 mt-1">
                                        ARS: {formatCurrency(cashFlowData.totalIngresosBancoARS, 'ARS')}
                                    </div>
                                </div>
                                <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Egreso Efectivo CBU</span>
                                    <div className="text-lg font-black font-mono text-rose-600 mt-1">
                                        ARS: {formatCurrency(cashFlowData.totalEgresosBancoARS, 'ARS')}
                                    </div>
                                </div>
                                <div className="bg-slate-900 text-white p-4 rounded-2xl shadow-xs">
                                    <span className="text-[10px] font-bold uppercase tracking-widest opacity-70 block">Flujo Neto Resultante</span>
                                    <div className="text-lg font-black font-mono text-white mt-1">
                                        ARS: {formatCurrency(cashFlowData.saldoCashFlowARS, 'ARS')}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* CARDS RETENCIONES SUFRIDAS */}
                        {hasQueried && isDbLoaded && moduloRaiz === 'RETENCIONES' && retencionesDiscriminadas && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 shrink-0">
                                <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Total Retenido (ARS)</span>
                                    <div className="text-lg font-black font-mono text-rose-600 mt-1">
                                        {formatCurrency(retencionesDiscriminadas.totalRetenidoARS, 'ARS')}
                                    </div>
                                </div>
                                {Object.entries(retencionesDiscriminadas.desglosePorTipoARS).map(([tipo, monto]) => (
                                    <div key={tipo} className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Ret. {tipo}</span>
                                        <div className="text-lg font-black font-mono text-slate-800 mt-1">
                                            {formatCurrency(monto, 'ARS')}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* TABLA PRINCIPAL DE FINANZAS */}
                        <div className="flex-grow overflow-hidden">
                            {hasQueried && isDbLoaded ? (
                                <TablePreviewFinanzas 
                                    registros={registrosProcesados}
                                    columnasSeleccionadas={columnasSeleccionadas}
                                    camposDisponibles={camposDisponibles}
                                    moduloRaiz={moduloRaiz}
                                />
                            ) : (
                                <div className="flex flex-col items-center justify-center p-12 text-center bg-white border border-slate-200/60 rounded-3xl max-w-xl mx-auto mt-16 shadow-[0_4px_25px_-5px_rgba(0,0,0,0.02)]">
                                    <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl mb-5 shadow-sm">
                                        <IconDatabase />
                                    </div>
                                    <h3 className="text-[15px] font-black uppercase tracking-wider text-slate-800">
                                        Centro de Control Financiero
                                    </h3>
                                    <p className="text-xs text-slate-400 max-w-sm mt-2 leading-relaxed">
                                        Selecciona el período o rango de fechas en la parte superior y haz clic en <span className="font-semibold text-slate-600">"Filtrar"</span> para procesar comprobantes y movimientos.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>

            {/* MODAL DE CARGA */}
            {loadingProgress.active && (
                <div className="fixed inset-0 bg-[#0c0f24]/60 z-[2000] flex items-center justify-center p-4 backdrop-blur-md animate-fade-in">
                    <div className="bg-white rounded-[28px] p-8 max-w-md w-full border border-slate-200/50 text-center space-y-6 shadow-2xl">
                        <div className="relative w-16 h-16 mx-auto flex items-center justify-center">
                            <div className="absolute inset-0 border-4 border-slate-100 rounded-full"></div>
                            <div className="absolute inset-0 border-4 border-t-[#01032d] border-r-[#01032d] rounded-full animate-spin"></div>
                        </div>
                        <div className="space-y-1.5">
                            <p className="text-[15px] font-extrabold text-slate-800">{loadingProgress.text}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportesFinanzas;